'use client'

import * as React from 'react'

import { applyDocumentColorScheme } from '@axilon-apps/libs-ui-shadcn'

import { useSettings } from '../hooks/use-settings'

import Rtl from './rtl'

interface Props {
  children: React.ReactNode
}

/**
 * Inject a global `transition: none !important` rule, force a reflow so
 * it takes effect synchronously, then strip it on the next animation
 * frame.
 *
 * This is the standard light/dark-swap trick that next-themes ships as
 * `disableTransitionOnChange` (and that Tailwind docs / Vercel / Linear
 * all use). Without it, individual elements with `transition-all` /
 * `transition-colors` (e.g. shadcn Button's base class, Card, etc.)
 * each animate their `background-color`, `border-color`, and `color`
 * over the same ~150ms but reach distinct CSS variables at different
 * commit times — producing the "cascading recolour" the user sees.
 *
 * Snapping to the new theme instantly is the cleanest UX: by the time
 * the next paint happens, every token is at its final value and
 * transitions are re-enabled for normal user interactions
 * (hovers / focus rings / etc.).
 */
function suppressTransitionsDuringThemeSwap() {
  const css = document.createElement('style')
  css.appendChild(
    document.createTextNode(
      '*, *::before, *::after { transition: none !important; animation-duration: 0ms !important; animation-delay: 0ms !important; }'
    )
  )
  document.head.appendChild(css)

  // Force a synchronous layout/style recomputation so the no-transition
  // rule applies *before* the attribute change below mutates the
  // cascade. Without this, the browser is free to batch the style
  // injection with the attribute change and the suppression is too late.
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  window.getComputedStyle(document.body).opacity

  // Resume normal transitions on the next animation frame — by then the
  // attribute change has flushed and the new theme is fully painted.
  window.requestAnimationFrame(() => {
    document.head.removeChild(css)
  })
}

function ThemeProvider({ children }: Props): React.JSX.Element {
  const { settings } = useSettings()

  // Drive every supported theme trigger on the <html> element from the
  // user's chosen colorScheme. Four things in lockstep:
  //   - `data-theme` attribute — the canonical selector our global.css
  //     scopes its dark-mode token overrides to
  //     (`[data-theme='dark'] { --bg: ...; --ink: ...; ... }`). This is
  //     also the source of truth for Tailwind v4's `dark:` variant
  //     (`@custom-variant dark`) declared at the top of global.css.
  //   - `data-joy-color-scheme` attribute — historically held over from
  //     Joy's `CssVarsProvider`. The shim is gone, but a few legacy
  //     ag-charts surfaces still check the attribute; keeping it as a
  //     pure mirror of `data-theme` costs nothing and prevents a
  //     regression on those surfaces.
  //   - `class="light|dark"` — fallback for any third-party Tailwind
  //     code that keys off the historical `.dark` class.
  //   - `color-scheme` style — tells the UA to render native form
  //     controls (scrollbars, default form widgets, link colors) in the
  //     matching scheme.
  // No CSS variable values live in JS — every colour comes from
  // global.css tokens; this effect only flips the selector hooks.
  // `suppressTransitionsDuringThemeSwap` prevents the per-element
  // transition cascade that would otherwise turn the colour-scheme
  // change into a half-second sequential recolour.
  React.useEffect(() => {
    suppressTransitionsDuringThemeSwap()
    applyDocumentColorScheme(settings.colorScheme)
  }, [settings.colorScheme])

  // Mirror `data-theme` for the brand-colour swap. global.css carries
  // explicit `[data-primary-color='crayolaBlue']` and `[data-primary-
  // color='seaGreen']` blocks that override --brand / --brand-soft /
  // --brand-fg / --brand-950 / --brand-gradient. The `default` value
  // has no matching block — the :root tokens already define those
  // defaults, so the cascade naturally falls through. Every downstream
  // alias (--primary, --ring, hover/focus states) derives from those,
  // so one attribute flip retints the entire surface. Same transition-
  // suppression trick — brand swaps cascade through every primary-
  // tinted surface (buttons, rings, active nav) and would stutter the
  // same way without it.
  React.useEffect(() => {
    suppressTransitionsDuringThemeSwap()
    document.documentElement.setAttribute('data-primary-color', settings.primaryColor)
  }, [settings.primaryColor])

  return <Rtl direction={settings.direction}>{children}</Rtl>
}

export default ThemeProvider
